import { useCallback } from 'react';

import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
  useIsFetching,
} from '@tanstack/react-query';

import { Dayjs } from 'dayjs';
import { isEmpty } from 'lodash';

import queryString from 'query-string';

import { handleError, ResponseType, translateDateIntoIsoString } from '@/utils';

import useP4RContext from '@/store/P4RContext';

import {
  getCurrentUserApi,
  getUserDataApi,
  getUserPreferenceApi,
  putUserPreferenceApi,
  getAppConfigApi,
  getLicenceAreasApi,
  getLicenceAreaApi,
  getLicenceAreaFaultBandsApi,
  getLicenceAreaDistrictsApi,
  getLicenceAreaDistrictsFaultAndWeatherForecastsBreakdownApi,
  getLicenceAreaTotalFaultAndWeatherForecastsBreakdownApi,
  getDistrictApi,
  getDistrictFaultAndWeatherForecastsApi,
  getDistrictFaultForecastsApi,
  getDistrictFaultForecastsByFaultBandApi,
  getLicenceAreaFaultForecastsApi,
  getLicenceAreaFaultForecastsByFaultBandApi,
  getLicenceAreaExtremeWeatherForecastApi,
  getWeatherEventSimulationBreakdownApi,
  getWeatherEventSimulationForecastApi,
  getWeatherEventSimulationForecastByFaultBandApi,
  getDistrictExtremeWeatherForecastApi,
  getDistrictFaultBandsApi,
  putDistrictFaultBandsApi,
  getDistrictHourlyWeatherForecastApi,
  getMarkerHourlyWeatherForecastApi,
  getWeatherEventsDetailsApi,
  getFaultsBreakdownApi,
  getNetworkImpactApi,
  getActionPlanApi,
  getUsersApi,
  postUserApi,
  putUserApi,
  deleteUserApi,
  unblockUserApi,
  getLicenceAreaRessourceCalculatorParametersApi,
  getDistrictRessourceCalculatorParametersApi,
  putRessourceCalculatorParametersApi,
  getMailSubscriptionsApi,
  postMailSubscriptionApi,
  putMailSubscriptionApi,
  deleteMailSubscriptionApi,
  getHorodateTimeApi,
  getWeatherEventsApi,
  putWeatherEventsUpdateApi,
  deleteWeatherEventApi,
  getWeatherEventActionPlanApi,
  postResourceCalculatorResultApi,
  getForecastQualityApi,
  postFeedbackApi,
  getDistrictSimilarEventsApi,
  getDistrictsSimilarityApi,
  getWindParticulesApi,
  getWindParticulesJSONApi,
  getUserPasswordApi,
  getMapShapesApi,
} from './services';

import {
  GET_APP_CONFIG_KEY,
  GET_MAP_SHAPES_KEY,
  GET_LICENCE_AREAS_QUERY_KEY,
  GET_LICENCE_AREA_QUERY_KEY,
  GET_LICENCE_AREA_FAULT_BANDS_QUERY_KEY,
  GET_LICENCE_AREA_DISTRICTS_QUERY_KEY,
  GET_LICENCE_AREA_DISTRICTS_FAULT_AND_WEATHER_FORECASTS_BREAKDOWN_QUERY_KEY,
  GET_DISTRICT_QUERY_KEY,
  GET_DISTRICT_FAULT_AND_WEATHER_FORECAST_QUERY_KEY,
  GET_DISTRICT_FAULT_FORECASTS_QUERY_KEY,
  GET_DISTRICT_FAULT_FORECASTS_BY_FAULT_BAND_QUERY_KEY,
  GET_DISTRICT_EXTREME_WEATHER_FORECAST_QUERY_KEY,
  GET_DISTRICT_FAULT_BANDS_QUERY_KEY,
  GET_DISTRICT_HOURLY_FORECAST_QUERY_KEY,
  GET_MARKER_HOURLY_FORECAST_QUERY_KEY,
  GET_FAULT_BREAKDOWN_KEY,
  GET_NETWORK_IMPACT_KEY,
  GET_ACTION_PLAN_KEY,
  GET_USERS_QUERY_KEY,
  GET_RESOURCE_CALCULATOR_PARAMETERS_QUERY_KEY,
  GET_MAIL_SUBCRIPTIONS_QUERY_KEY,
  GET_CURRENT_USER_QUERY_KEY,
  GET_WEATHER_EVENT_DETAILS_QUERY_KEY,
  GET_WEATHER_EVENTS_QUERY_KEY,
  GET_WEATHER_EVENT_LICENCE_AREA_QUERY_KEY,
  GET_RESOURCE_CALCULATOR_RESULTS_QUERY_KEY,
  GET_FORECAST_QUALITY,
  GET_HORODATETIME_KEY,
  GET_DISTRICT_SIMILAR_EVENTS_QUERY_KEY,
  GET_DISTRICT_SIMILARITY_QUERY_KEY,
  GET_USER_DATA_KEY,
  GET_SIMULATION_FORECAST_BREAKDOWN_QUERY_KEY,
  GET_SIMULATION_FORECAST_QUERY_KEY,
  GET_SIMULATION_FORECAST_BY_FAULT_BAND_QUERY_KEY,
  GET_WIND_PARTICULES,
  GET_WIND_PARTICULES_JSON,
  GET_USER_PREFERENCES_KEY,
  GET_USER_PASSWORD_KEY,
} from './constants';

import { typeOfActionValue } from '@/utils/constants';

// Current User Data

export const useGetCurrentUser = (enabled: boolean) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_CURRENT_USER_QUERY_KEY],
    queryFn: getCurrentUserApi,
    enabled,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_CURRENT_USER_QUERY_KEY],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useGetUserData = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_USER_DATA_KEY],
    queryFn: getUserDataApi,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_USER_DATA_KEY],
    });
  };

  return {
    ...query,
    refetchData,
  };
};
export const useGetUserPassword = (userEmail: string, enabled: boolean) =>
  useQuery({
    queryKey: [GET_USER_PASSWORD_KEY, userEmail],
    queryFn: () => getUserPasswordApi(userEmail),
    enabled,
  });

export const useGetUserPreferences = (enabled: boolean) =>
  useQuery({
    queryKey: [GET_USER_PREFERENCES_KEY],
    queryFn: getUserPreferenceApi,
    enabled,
  });

export const usePutUserPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putUserPreferenceApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_USER_PREFERENCES_KEY],
      });
    },
  });
};

export const useGetHorodateTime = (
  enabled: boolean,
  startDateOverride?: Dayjs,
) => {
  const { startDate, timelineMode } = useP4RContext();

  const effectiveStartDate = startDateOverride || startDate?.[0];
  const queryEnabled = !!enabled && !!effectiveStartDate;
  const isLive = !startDateOverride && timelineMode === 'live';

  return useQuery({
    queryKey: [
      GET_HORODATETIME_KEY,
      isLive ? 'live' : translateDateIntoIsoString(effectiveStartDate),
    ],
    queryFn: () =>
      getHorodateTimeApi(translateDateIntoIsoString(effectiveStartDate)),
    refetchInterval: isLive ? 60 * 1000 : false,
    refetchIntervalInBackground: isLive,
    staleTime: isLive ? 0 : Infinity,
    enabled: queryEnabled,
  });
};

/**
 * Hook to check if any horodatetime request is loading
 * @returns true if at least one request is loading, false otherwise
 */
export const useHorodateTimeLoading = (): boolean =>
  !!useIsFetching({
    queryKey: [GET_HORODATETIME_KEY],
  });

// Licences & Districts Data

export const useGetLicenceAreas = (enabled: boolean) =>
  useQuery({
    queryKey: [GET_LICENCE_AREAS_QUERY_KEY],
    queryFn: getLicenceAreasApi,
    enabled,
  });

/**
 * Hook to get the name of a district from the licence areas data
 * @param {string} districtId - The ID of the district
 * @returns {string | undefined} The name of the district
 */
export const useGetDistrictName = () => {
  const { data: licencesAreasData } = useGetLicenceAreas(true);
  const getDistrictName = useCallback(
    (districtId: string) => {
      let districtName: string | undefined;
      licencesAreasData?.forEach((licenceArea) => {
        licenceArea.districts.forEach((district) => {
          if (district.districtId === districtId) {
            districtName = district.districtName;
          }
        });
      });
      return districtName;
    },
    [licencesAreasData],
  );
  return getDistrictName;
};

// App Config

export const useGetAppConfig = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_APP_CONFIG_KEY],
    queryFn: getAppConfigApi,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_APP_CONFIG_KEY],
    });
  };

  return {
    ...query,
    refetchData,
  };
};
export const useGetMapShapes = () =>
  useQuery({
    queryKey: [GET_MAP_SHAPES_KEY],
    queryFn: getMapShapesApi,
  });

export const useGetLicenceArea = (licenceAreaId: null | string | boolean) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_LICENCE_AREA_QUERY_KEY, licenceAreaId],
    queryFn: () => getLicenceAreaApi(licenceAreaId as string),
    enabled: !!licenceAreaId,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_LICENCE_AREA_QUERY_KEY, licenceAreaId],
      exact: true,
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useGetDistrict = (districtId: string | undefined) =>
  useQuery({
    queryKey: [GET_DISTRICT_QUERY_KEY, districtId],
    queryFn: () => getDistrictApi(districtId as string),
    enabled: districtId !== undefined,
  });

export const useGetAllLicenceAreaDistricts = (licenceAreaIds: string[]) =>
  useQueries({
    queries: licenceAreaIds.map((id) => ({
      queryKey: ['GET_LICENCE_AREA_DISTRICTS_QUERY_KEY', id],
      queryFn: () => getLicenceAreaDistrictsApi(id),
      enabled: !!licenceAreaIds.length,
    })),
  });

export function useGetAllDistricts() {
  const { data: licenceAreas } = useGetLicenceAreas(true) as {
    data: ILicenceAreaMin[] | undefined;
    isLoading: boolean;
  };

  const districts = useGetAllLicenceAreaDistricts(
    licenceAreas ? licenceAreas.map((area) => area.id) : [],
  );

  return districts
    .filter((district) => district.data)
    .map((district) => district.data)
    .flat();
}

export const useGetDistrictFaultAndWeatherForecasts = (
  districtId: string | undefined,
  enabled?: boolean,
) => {
  const queryClient = useQueryClient();
  const { data: horodatetime } = useGetHorodateTime(!!enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;

  const query = useQuery({
    queryKey: [
      GET_DISTRICT_FAULT_AND_WEATHER_FORECAST_QUERY_KEY,
      districtId,
      weatherForecastHorodatetime,
    ],
    queryFn: () =>
      getDistrictFaultAndWeatherForecastsApi(districtId as string, {
        horodatetime: weatherForecastHorodatetime,
      }),
    enabled:
      districtId !== undefined && !!weatherForecastHorodatetime && enabled,
  });
  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_DISTRICT_FAULT_AND_WEATHER_FORECAST_QUERY_KEY, districtId],
      exact: true,
    });
  };
  return {
    ...query,
    refetchData,
  };
};

export const useGetDistrictSimilarity = (
  licenceAreaId: string | boolean | null,
  weatherEventId: string | undefined,
  enabled?: boolean,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      GET_DISTRICT_SIMILARITY_QUERY_KEY,
      weatherEventId,
      licenceAreaId,
    ],
    queryFn: () =>
      getDistrictsSimilarityApi(
        weatherEventId as string,
        licenceAreaId as string,
      ),
    enabled: weatherEventId !== undefined && !!licenceAreaId && enabled,
  });
  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [
        GET_DISTRICT_SIMILARITY_QUERY_KEY,
        weatherEventId,
        licenceAreaId,
      ],
      exact: true,
    });
  };
  return {
    ...query,
    refetchData,
  };
};
export const useGetDistrictSimilarEvents = (districtId: string | undefined) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_DISTRICT_SIMILAR_EVENTS_QUERY_KEY, districtId],
    queryFn: () => getDistrictSimilarEventsApi(districtId as string),
    enabled: districtId !== undefined,
  });
  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_DISTRICT_SIMILAR_EVENTS_QUERY_KEY, districtId],
      exact: true,
    });
  };
  return {
    ...query,
    refetchData,
  };
};

export const useGetDistrictFaultForecasts = (
  districtId: string,
  params: any,
  enabled: boolean,
) => {
  const { data: horodatetime } = useGetHorodateTime(enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;
  const newParams = {
    ...params,
    horodatetime: weatherForecastHorodatetime,
  };
  const query = useQuery({
    queryKey: [GET_DISTRICT_FAULT_FORECASTS_QUERY_KEY, districtId, newParams],
    queryFn: () => getDistrictFaultForecastsApi(districtId, newParams),
    enabled: enabled && !!weatherForecastHorodatetime,
  });

  return query;
};
export const useGetDistrictFaultForecastsByFaultBand = (
  districtId: string,
  params: {
    startDatetime?: string;
    timeResolution?: string;
  },
  enabled: boolean,
) => {
  const { data: horodatetime } = useGetHorodateTime(enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;
  const newParams = {
    ...params,
    horodatetime: weatherForecastHorodatetime,
  };
  const query = useQuery({
    queryKey: [
      GET_DISTRICT_FAULT_FORECASTS_BY_FAULT_BAND_QUERY_KEY,
      districtId,
      newParams,
    ],
    queryFn: () =>
      getDistrictFaultForecastsByFaultBandApi(districtId, newParams),
    enabled:
      !!districtId &&
      !!params.startDatetime &&
      !!weatherForecastHorodatetime &&
      enabled,
  });

  return query;
};

export const useGetLicenceAreaFaultForecasts = (
  licenceAreaId: string,
  params: {
    startDatetime?: string;
    timeResolution?: string;
  },
  enabled: boolean,
) => {
  const { data: horodatetime } = useGetHorodateTime(enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;
  const newParams = {
    ...params,
    horodatetime: weatherForecastHorodatetime,
  };
  const query = useQuery({
    queryKey: [
      'GET_LICENCE_AREA_FAULT_FORECASTS_QUERY_KEY',
      licenceAreaId,
      newParams,
    ],
    queryFn: () =>
      getLicenceAreaFaultForecastsApi(licenceAreaId, newParams as any),
    enabled: enabled && !!weatherForecastHorodatetime,
  });

  return query;
};

export const useGetLicenceAreaFaultForecastsByFaultBand = (
  licenceAreaId: string,
  params: {
    startDatetime?: string;
    timeResolution?: string;
  },
  enabled: boolean,
) => {
  const { data: horodatetime } = useGetHorodateTime(enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;
  const newParams = {
    ...params,
    horodatetime: weatherForecastHorodatetime,
  };
  const query = useQuery({
    queryKey: [
      'GET_LICENCE_AREA_FAULT_FORECASTS_BY_FAULT_BAND_QUERY_KEY',
      licenceAreaId,
      newParams,
    ],
    queryFn: () =>
      getLicenceAreaFaultForecastsByFaultBandApi(licenceAreaId, newParams),
    enabled: !!params.startDatetime && !!weatherForecastHorodatetime && enabled,
  });

  return query;
};

export const useGetLicenceAreaExtremeWeatherForecast = (
  licenceAreaId: string,
  params: {
    targetDatetime?: string;
  },
  enabled: boolean,
) => {
  const { data: horodatetime } = useGetHorodateTime(enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;
  const newParams = {
    ...params,
    horodatetime: weatherForecastHorodatetime,
  };
  const query = useQuery({
    queryKey: [
      'GET_LICENCE_AREA_EXTREME_WEATHER_FORECAST_QUERY_KEY',
      licenceAreaId,
      newParams,
    ],
    queryFn: () =>
      getLicenceAreaExtremeWeatherForecastApi(licenceAreaId, newParams as any),
    enabled: enabled && !!weatherForecastHorodatetime,
  });

  return query;
};

export const useGetWeatherEventSimulationBreakdown = (
  licenceAreaId: string | boolean | null,
  weatherEventId: string | undefined,
  horodatetime?: string,
  enabled?: boolean,
) => {
  const query = useQuery({
    queryKey: [
      GET_SIMULATION_FORECAST_BREAKDOWN_QUERY_KEY,
      licenceAreaId,
      weatherEventId,
      horodatetime,
    ],
    queryFn: () =>
      getWeatherEventSimulationBreakdownApi(
        licenceAreaId as string,
        weatherEventId as string,
        { horodatetime },
      ),
    enabled: !!licenceAreaId && !!weatherEventId && !!horodatetime && enabled,
  });

  return { ...query };
};

export const useGetWeatherEventSimulationForecast = (
  districtId: string | undefined,
  weatherEventId: string | undefined,
  params: {
    startDatetime: string;
    timeResolution: string;
    horodatetime?: string;
  },
  enabled?: boolean,
) => {
  const query = useQuery({
    queryKey: [
      GET_SIMULATION_FORECAST_QUERY_KEY,
      districtId,
      weatherEventId,
      params,
    ],
    queryFn: () =>
      getWeatherEventSimulationForecastApi(
        districtId as string,
        weatherEventId as string,
        params,
      ),
    retry: 0,
    enabled:
      !!districtId && !!weatherEventId && !!params.horodatetime && enabled,
  });

  return query;
};

export const useGetWeatherEventSimulationForecastByFaultBand = (
  districtId: string | undefined,
  weatherEventId: string | undefined,
  params: {
    startDatetime?: string;
    horodatetime?: string;
  },
  enabled?: boolean,
) => {
  const query = useQuery({
    queryKey: [
      GET_SIMULATION_FORECAST_BY_FAULT_BAND_QUERY_KEY,
      districtId,
      weatherEventId,
      params,
    ],
    queryFn: () =>
      getWeatherEventSimulationForecastByFaultBandApi(
        districtId as string,
        weatherEventId as string,
        params,
      ),
    enabled:
      !!districtId &&
      !!weatherEventId &&
      !!params.startDatetime &&
      !!params.horodatetime &&
      enabled,
  });

  return query;
};

export const useGetDistrictExtremeWeatherForecast = (
  districtId: string,
  params: {
    targetDatetime?: string;
  },
  enabled: boolean,
) => {
  const { data: horodatetime } = useGetHorodateTime(!!enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;
  const newParams = {
    ...params,
    horodatetime: weatherForecastHorodatetime,
  };
  const query = useQuery({
    queryKey: [
      GET_DISTRICT_EXTREME_WEATHER_FORECAST_QUERY_KEY,
      districtId,
      newParams,
    ],
    queryFn: () => getDistrictExtremeWeatherForecastApi(districtId, newParams),
    enabled:
      !!params.targetDatetime && !!weatherForecastHorodatetime && enabled,
  });

  return query;
};
export const useGetDistrictHourlyWeatherForecast = (
  districtId: string | undefined,
  params:
    | {
        startDatetime: string;
        endDatetime: string;
      }
    | object,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_DISTRICT_HOURLY_FORECAST_QUERY_KEY, districtId, params],
    queryFn: () =>
      getDistrictHourlyWeatherForecastApi(
        districtId as string,
        params as {
          startDatetime: string;
          endDatetime: string;
        },
      ),
  });
  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_DISTRICT_HOURLY_FORECAST_QUERY_KEY, districtId, params],
      exact: true,
    });
  };
  return {
    ...query,
    refetchData,
  };
};

export const useGetMarkerHourlyWeatherForecast = (
  markerCoordinates: {
    lat: number;
    lng: number;
  } | null,
  params:
    | {
        startDatetime: string;
        endDatetime: string;
        weatherHorodatetime: string;
        weatherVariable: string;
      }
    | (object & {
        weatherVariable?: string;
      }),
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      GET_MARKER_HOURLY_FORECAST_QUERY_KEY,
      markerCoordinates,
      // es-lint-disable-next-line @typescript-eslint/no-unsafe-assignment
      params.weatherVariable,
    ],
    queryFn: () =>
      getMarkerHourlyWeatherForecastApi(
        markerCoordinates as {
          lat: number;
          lng: number;
        },
        params as {
          startDatetime: string;
          endDatetime: string;
          weatherHorodatetime: string;
          weatherVariable: string;
        },
      ),
    enabled: !!markerCoordinates && !isEmpty(params),
  });

  const refetchData = () => {
    queryClient.cancelQueries({
      queryKey: [GET_MARKER_HOURLY_FORECAST_QUERY_KEY],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useGetLicenceAreaFaultBands = (
  licenceAreaId: string | boolean | null,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_LICENCE_AREA_FAULT_BANDS_QUERY_KEY, licenceAreaId],
    queryFn: () => getLicenceAreaFaultBandsApi(licenceAreaId as string),
    enabled: !!licenceAreaId,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_LICENCE_AREA_FAULT_BANDS_QUERY_KEY],
      exact: true,
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useGetDistrictFaultBands = (districtId: string | undefined) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_DISTRICT_FAULT_BANDS_QUERY_KEY, districtId],
    queryFn: () => getDistrictFaultBandsApi(districtId as string),
    enabled: districtId !== undefined,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_DISTRICT_FAULT_BANDS_QUERY_KEY, districtId],
      exact: true,
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const usePutDistrictFaultBands = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putDistrictFaultBandsApi,
    onSuccess: (_: any, variables) => {
      queryClient.invalidateQueries({
        queryKey: [GET_DISTRICT_FAULT_BANDS_QUERY_KEY, variables.districtId],
      });
    },
  });
};

export const useGetLicenceAreaDistricts = (
  licenceAreaId: null | string | boolean,
  enabled: boolean,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_LICENCE_AREA_DISTRICTS_QUERY_KEY, licenceAreaId],
    queryFn: () => getLicenceAreaDistrictsApi(licenceAreaId as string),
    enabled: !!licenceAreaId && enabled,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_LICENCE_AREA_DISTRICTS_QUERY_KEY],
      exact: true,
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useGetLicenceAreaDistrictsFaultAndWeatherForecastsBreakdown = (
  licenceAreaId: null | string | boolean,
  enabled?: boolean,
) => {
  const queryClient = useQueryClient();
  const { data: horodatetime } = useGetHorodateTime(!!enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;

  const query = useQuery({
    queryKey: [
      GET_LICENCE_AREA_DISTRICTS_FAULT_AND_WEATHER_FORECASTS_BREAKDOWN_QUERY_KEY,
      licenceAreaId,
      weatherForecastHorodatetime,
    ],
    queryFn: () =>
      getLicenceAreaDistrictsFaultAndWeatherForecastsBreakdownApi(
        licenceAreaId as string,
        { horodatetime: weatherForecastHorodatetime },
      ),
    enabled: !!licenceAreaId && !!weatherForecastHorodatetime && enabled,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [
        GET_LICENCE_AREA_DISTRICTS_FAULT_AND_WEATHER_FORECASTS_BREAKDOWN_QUERY_KEY,
      ],
      exact: true,
    });
  };
  return {
    ...query,
    refetchData,
  };
};

export const useGetLicenceAreaTotalFaultAndWeatherForecastsBreakdown = (
  licenceAreaId: string | boolean | null,
  enabled?: boolean,
) => {
  const { data: horodatetime } = useGetHorodateTime(!!enabled);
  const weatherForecastHorodatetime = horodatetime?.weatherForecastHorodatetime;

  return useQuery({
    queryKey: [
      'GET_LICENCE_AREA_TOTAL_FAULT_AND_WEATHER_FORECASTS_BREAKDOWN_QUERY_KEY',
      licenceAreaId,
      weatherForecastHorodatetime,
    ],
    queryFn: () =>
      getLicenceAreaTotalFaultAndWeatherForecastsBreakdownApi(
        licenceAreaId as string,
        { horodatetime: weatherForecastHorodatetime },
      ),
    enabled: !!licenceAreaId && !!weatherForecastHorodatetime && enabled,
  });
};

export const useGetWeatherEventDetails = (
  weatherEventId?: string,
  enabled?: boolean,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_WEATHER_EVENT_DETAILS_QUERY_KEY, weatherEventId],
    queryFn: () => getWeatherEventsDetailsApi(weatherEventId as string),
    enabled: (enabled === undefined || enabled) && !!weatherEventId,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_WEATHER_EVENT_DETAILS_QUERY_KEY, weatherEventId],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useGetFaultsBreakdown = (
  licenceAreaId: null | string | boolean,
  weatherEventId: string | undefined,
  enabled: boolean,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_FAULT_BREAKDOWN_KEY, licenceAreaId, weatherEventId],
    queryFn: () =>
      getFaultsBreakdownApi(licenceAreaId as string, weatherEventId as string),
    enabled: !!licenceAreaId && !!weatherEventId && enabled,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_FAULT_BREAKDOWN_KEY, licenceAreaId, weatherEventId],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useGetNetworkImpact = (
  weatherEventId: string | undefined,
  licenceAreaId: any,
  typeOfAction = typeOfActionValue.edit,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_NETWORK_IMPACT_KEY, weatherEventId, licenceAreaId],
    queryFn: () => getNetworkImpactApi({ weatherEventId, licenceAreaId }),
    enabled:
      !!weatherEventId &&
      !!licenceAreaId &&
      typeOfAction === typeOfActionValue.edit,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_NETWORK_IMPACT_KEY, weatherEventId, licenceAreaId],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useGetActionPlan = (
  licenceAreaSelected: null | string | boolean,
  weatherEventId: string | undefined,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_ACTION_PLAN_KEY, licenceAreaSelected, weatherEventId],
    queryFn: () =>
      getActionPlanApi(licenceAreaSelected as string, weatherEventId as string),
    enabled: !!licenceAreaSelected && !!weatherEventId,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_ACTION_PLAN_KEY, licenceAreaSelected, weatherEventId],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

// Users Management
export const useGetUsers = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_USERS_QUERY_KEY],
    queryFn: getUsersApi,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_USERS_QUERY_KEY],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const usePostUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_USERS_QUERY_KEY],
      });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

export const usePutUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_USERS_QUERY_KEY] });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_USERS_QUERY_KEY] });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

export const useUnblockUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unblockUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_USERS_QUERY_KEY] });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

/// Mail Subscription

export const useGetMailSubscriptions = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_MAIL_SUBCRIPTIONS_QUERY_KEY],
    queryFn: getMailSubscriptionsApi,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_MAIL_SUBCRIPTIONS_QUERY_KEY],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const usePostMailSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postMailSubscriptionApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_MAIL_SUBCRIPTIONS_QUERY_KEY],
      });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

export const usePutMailSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putMailSubscriptionApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_MAIL_SUBCRIPTIONS_QUERY_KEY],
      });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};
export const useDeleteMailSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMailSubscriptionApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_MAIL_SUBCRIPTIONS_QUERY_KEY],
      });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

export const usePostFeeback = () =>
  useMutation({
    mutationFn: postFeedbackApi,

    onError: (error: ResponseType) => {
      handleError(error);
    },
  });

//// User Preferences

export const useGetResourceCalculatorParameters = ({
  ressourceType,
  id,
}: {
  ressourceType: 'licenceArea' | 'district';
  id?: string;
}) =>
  useQuery<
    | LicenceAreaResourceCalculatorParameters
    | DistrictResourceCalculatorParameters
  >({
    queryKey: [GET_RESOURCE_CALCULATOR_PARAMETERS_QUERY_KEY, ressourceType, id],
    queryFn: () =>
      ressourceType === 'licenceArea'
        ? getLicenceAreaRessourceCalculatorParametersApi(id)
        : getDistrictRessourceCalculatorParametersApi(id),
    enabled: !!id,
  });

export const usePutResourceCalculatorParameters = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putRessourceCalculatorParametersApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_RESOURCE_CALCULATOR_PARAMETERS_QUERY_KEY, 'licenceArea'],
      });
      queryClient.removeQueries({
        queryKey: [GET_RESOURCE_CALCULATOR_PARAMETERS_QUERY_KEY, 'district'],
      });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

export const usePostResourceCalculatorResults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postResourceCalculatorResultApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_RESOURCE_CALCULATOR_RESULTS_QUERY_KEY],
      });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

// Weather events validation/edition form

export const useGetWeatherEvents = (params: any) => {
  const queryClient = useQueryClient();

  const newParams = {
    ...params,
  };

  if (newParams.total) {
    delete newParams.total;
  }

  const qs = queryString.stringify(newParams);

  const query = useQuery({
    queryKey: [GET_WEATHER_EVENTS_QUERY_KEY, newParams],
    queryFn: () => getWeatherEventsApi(qs),
    enabled: !!newParams.status?.length,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_WEATHER_EVENTS_QUERY_KEY, newParams],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const usePutWeatherEventsUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putWeatherEventsUpdateApi,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [GET_WEATHER_EVENTS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [
          GET_WEATHER_EVENT_DETAILS_QUERY_KEY,
          variables.weatherEventId,
        ],
      });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

export const useGetWeatherEventActionPlan = ({
  eventId,
  licenceAreaId,
  typeOfAction = typeOfActionValue.edit,
}: {
  eventId: string;
  licenceAreaId: string;
  typeOfAction: string;
}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [GET_WEATHER_EVENT_LICENCE_AREA_QUERY_KEY, eventId],
    queryFn: () => getWeatherEventActionPlanApi({ eventId, licenceAreaId }),
    enabled:
      !!eventId && !!licenceAreaId && typeOfAction === typeOfActionValue.edit,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_WEATHER_EVENT_LICENCE_AREA_QUERY_KEY, eventId],
      exact: true,
    });
  };

  return {
    ...query,
    refetchData,
  };
};

export const useDeleteWeatherEvents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWeatherEventApi,
    onSuccess: (_, weatherEventId) => {
      queryClient.invalidateQueries({
        queryKey: [GET_WEATHER_EVENTS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_WEATHER_EVENT_DETAILS_QUERY_KEY, weatherEventId],
      });
    },
    onError: (error: ResponseType) => {
      handleError(error);
    },
  });
};

// forecast quality
export const useGetForecastQuality = (params: any) => {
  const queryClient = useQueryClient();
  const qs = queryString.stringify(params);

  const query = useQuery({
    queryKey: [GET_FORECAST_QUALITY, params],
    queryFn: () => getForecastQualityApi(qs),
    enabled:
      params?.districtIds?.length > 0 &&
      !!params.startRegulatoryYear &&
      !!params.endRegulatoryYear,
  });

  const refetchData = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_FORECAST_QUALITY],
    });
  };

  return {
    ...query,
    refetchData,
  };
};

// wind particuless
export const useGetWindParticules = (
  params?: {
    weatherHorodatetime?: string;
    datetime?: string;
    horizon?: number;
  },
  endpointUrl?: string,
  removeParticules?: boolean,
) => {
  const { data: firstQuery } = useQuery({
    queryKey: [GET_WIND_PARTICULES, params],
    queryFn: () =>
      getWindParticulesApi(
        params as {
          weatherHorodatetime: string;
          horizon: number;
        },
        endpointUrl as string,
      ),
    enabled: !!params && !removeParticules,
  });

  const url = firstQuery?.data?.jsonUrl;

  const secondQuery = useQuery({
    queryKey: [GET_WIND_PARTICULES_JSON, url],
    queryFn: () => getWindParticulesJSONApi(url),
    enabled: !!url,
  });

  return secondQuery;
};
